import { Component, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Store } from '@ngrx/store';
import { Epicrisis, EpicrisisCaseData } from './interfaces/epicrisis.interface';
import { formatDate } from '@angular/common';
import { catchError, distinctUntilChanged, forkJoin, of, Subscription } from 'rxjs';
import { selectCaseInfoForEpicrisis } from '../../../../../store/selectors/case-epicrisis-report.selector';
import { ParameterService } from '../../../../../../../shared/services/parameter/parameter.service';
import { FormTab, FormTabItem } from '../../inspectation-informations/interfaces/inspectation-informations.interface';
import { FormTabItemsGetTypeEnum, FormTabService } from '../../../../../../management/application-management/form-tab/services/form-tab.service';
import { EpicrisisInputComponent } from './epicrisis-input/epicrisis-input.component';
import { EpicrisisHelperService } from './services/epicrisis-helper.service';
import { ApiService } from '../../../../../../../shared/services/api/api.service';
import { EpicrisisInput, EpicrisisRecord } from './interfaces/epicrisis-report-field.interface';
import { SendActionPayload, SendMailSmsComponent } from '../../../../../../../ui-components/send-mail-sms/send-mail-sms.component';

@Component({
  selector: 'app-case-epicrisis',
  templateUrl: './case-epicrisis.component.html',
  styleUrl: './case-epicrisis.component.scss'
})
export class CaseEpicrisisComponent {

  @ViewChildren(EpicrisisInputComponent) 
  inputComponents!: QueryList<EpicrisisInputComponent>;

  subscriptions = new Subscription();
  showPopup: boolean = false;
  displayMode: boolean = false;
  isApproved: boolean = false;

  caseId: number;
  approvalDate: string = '';
  approvedBy: string | undefined;
  canceledBy: string;
  canceledDate: Date;

  epicrisis: Epicrisis = this.helperService.getEmptyEpicrisisData();
  epicrisisMainCopy: Epicrisis;

  loading: boolean = false;
  activeCard: number = 0;

  allRecords: EpicrisisRecord[] = [];
  approvedData: EpicrisisInput[] = [];

  @ViewChild('epicrisisMail') epicrisisMail!: SendMailSmsComponent;

  showEmailDialog = false;

  constructor(
    private readonly store: Store,
    private readonly parameterService: ParameterService,
    private readonly formTabService: FormTabService,
    private readonly helperService: EpicrisisHelperService,
    private readonly apiService: ApiService,
    
  ) {
    
  }

  open(caseId: number) {
    this.showPopup = true;
    this.loading = true;
    this.isApproved = false;
    this.caseId = caseId;

    // control if any exists
    this.getPreviousRecords();
  }

  private getPreviousRecords() {
    this.apiService.get<EpicrisisRecord[]>(`epicrisis/case/${this.caseId}`).subscribe( res => {
      if(res.length === 0) {
        this.createNewEpicrisis();
      } else {
        this.allRecords = res.filter( record => record.canceledBy );
        const current = res.find( record => !record.canceledBy );
        this.isApproved = !!current;
        if(this.isApproved) {
          this.approvalDate = formatDate(current ? current.approvalDate : new Date(), 'dd.MM.yyyy', 'tr');
          this.approvedBy = current?.approvedBy ? current.approvedBy.firstName+' '+current.approvedBy.lastName : '';

          this.approvedData = current ? current.fieldData : [];
                  const contacts = current?.cases?.patientInformations?.contacts || [];
          setTimeout( () => {
            this.epicrisis = this.helperService.convertToEpicrisis(current ? current.fieldData : [], this.inputComponents, contacts);
          }, 200);
          this.displayMode = true;
        } else {;
          this.createNewEpicrisis();
        
        }
      }
      this.loading = false;
    });
  }

  private createNewEpicrisis() {
    this.displayMode = false;
    this.activeCard = 0;

    this.approvalDate = formatDate(new Date(), 'dd.MM.yyyy', 'tr');
    this.subscriptions.add(
      this.store
        .select(selectCaseInfoForEpicrisis)
        .pipe(distinctUntilChanged())
        .subscribe((data: EpicrisisCaseData) => {
          this.epicrisis = {...this.epicrisis, ...data};
        })
    );

    this.getDoctorInfo();
    this.getQuestionsDirty();
  }

  private getDoctorInfo() {
    this.apiService.get<{doctor: string}>(`epicrisis/doctorInfo/${this.caseId}`).subscribe(res => {
      this.epicrisis.doctor = res.doctor;
    });
  }

  approve() {
    // controls first
    this.isApproved = true;
    this.displayMode = true;

    let records = this.helperService.convertToInputs(this.epicrisis);
    for(const input of this.inputComponents){
       const data = input.reportSelf();
      const match = records.find( record => record.title === data.label);
      if(match) {
        match.value = data.value;
        match.isActive = data.isActive;
      }
    }

    this.apiService.post<EpicrisisRecord>('epicrisis/approve', {
        cases: this.caseId,   
        fieldData: records
    }).subscribe(res => {
      // approved!!!
      this.loading = true;
      this.getPreviousRecords();
    });
  }

  cancelApprove() {
    this.apiService.get(`epicrisis/cancel/${this.caseId}`).subscribe(res => {
      // cancelApprove!!!
      this.approvedBy = undefined;
      this.loading = true;
      this.getPreviousRecords();
    });
  }

  goToPdf() {

    this.helperService.generateEpicrisisPdf(this.approvedData, this.approvalDate);
    
  }

  cardClicked(index: number) {
    if(this.activeCard === index) {
      return;
    }
    this.activeCard = index;

    if(this.activeCard === 0) {
      this.getPreviousRecords();
      return;
    }

    this.epicrisis = this.helperService.convertToEpicrisis(this.allRecords[index-1].fieldData, this.inputComponents);
    this.displayMode = true;
  }

  private getQuestionsDirty() {
    forkJoin({
      formId: this.parameterService
        .getParameterValueByKeyname('case-form-id')
        .pipe(catchError(() => of(null))),
      formMenuId: this.parameterService
        .getParameterValueByKeyname('case-form-menu-id')
        .pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ formId, formMenuId }) => {
        const caseFormId = Number.isNaN(Number(formId?.value))
          ? null
          : Number(formId?.value);
        const caseFormMenuId = Number.isNaN(Number(formMenuId?.value))
          ? null
          : Number(formMenuId?.value);
        if (caseFormId && caseFormMenuId && this.caseId) {
          this.formTabService
            .getAllTabItemsByFormIdAndFormMenuId({
              caseId: this.caseId,
              formId: caseFormId,
              formMenuId: caseFormMenuId,
              type: FormTabItemsGetTypeEnum.QUESTION_ANSWER,
            })
            .subscribe({
              next: (res) => {
                this.dirtyParser(res);
                this.loading = false;
              }
            });
        }
      },
      error: (err) => {
        console.error('Parametreler alınırken hata oluştu:', err);
        this.loading = false;
      },
    });
  }

  private dirtyParser(tabs: FormTab[]) {

    for (const tab of tabs) {
      for (const item of tab.formTabItems) {
        this.dirtyParserCases(item);
      }
    }
    this.organizeCases();

  }

  /**
   * Tek bir soru objesini alarak eğer kullanılacak veriler ile eşleşiyor ise ilgili değişkene atama işlemini yapar.
   */
  private dirtyParserCases(item: FormTabItem) {
    switch (item.title) {
      case 'Hasta Şikayeti':
        this.epicrisis.complaint = item.answerValue;
        break;
      case 'Hasta Anamnezi':
        this.epicrisis.anamnesis = item.answerValue;
        break;
      case 'Pupiller':
        this.epicrisis.pupils = item.answerValue ?? '';
        break;
      case 'Deri':
        this.epicrisis.skin = item.answerValue ?? '';
        break;
      case 'Kurtarılma Yapıldı Mı?':
        this.epicrisis.isResqued = item.answerValue ?? '';
        break;
      case 'Solunum':
        this.epicrisis.respiratory = item.answerValue ?? '';
        break;
      case 'Cilt Muayenesi':
        this.epicrisis.skinExaminationAnswer = item.answerValue ?? '';
        this.epicrisis.skinExaminationDescription = item.description ?? '';
        break;
      case 'Baş - Boyun Muayenesi':
        this.epicrisis.headExaminationAnswer = item.answerValue ?? '';
        this.epicrisis.headExaminationDescription = item.description ?? '';
        break;
      case 'Solunum Sistemi Muayenesi':
        this.epicrisis.respiratoryExaminationAnswer = item.answerValue ?? '';
        this.epicrisis.respiratoryExaminationDescription = item.description ?? '';
        break;
      case 'Kardiyovasküler Sistem Muayenesi':
        this.epicrisis.cardiovascularExaminationAnswer = item.answerValue ?? '';
        this.epicrisis.cardiovascularExaminationDescription = item.description ?? '';
        break;
      case 'Batın Muayenesi':
        this.epicrisis.abdominalExaminationAnswer = item.answerValue ?? '';
        this.epicrisis.abdominalExaminationDescription = item.description ?? '';
        break;
      case 'Nörolojik Muayene':
        this.epicrisis.neurologicalExaminationAnswer = item.answerValue ?? '';
        this.epicrisis.neurologicalExaminationDescription = item.description ?? '';
        break;
      case 'Ekstremetiler':
        this.epicrisis.extremesAnswer = item.answerValue ?? '';
        this.epicrisis.extremesDescription = item.description ?? '';
        break;
      case 'Tedavi':
        this.epicrisis.treatment = item.answerValue ?? '';
        break;
      case 'Glasgow Koma Skalası - Motor':
        this.epicrisis.motor = item.answerValue ?? '';
        this.epicrisis.motorScore = item.typeDataParameter.parameterData.map( (p: any) => p.valueObject).find( (e: any) => e.name?.value === item.answerValue)?.score?.value;
        break;
      case 'Glasgow Koma Skalası - Verbal':
        this.epicrisis.verbal = item.answerValue ?? '';
        this.epicrisis.verbalScore = item.typeDataParameter.parameterData.map( (p: any) => p.valueObject).find( (e: any) => e.name?.value === item.answerValue)?.score?.value;
        break;
      case 'Glasgow Koma Skalası - Göz Açma':
        this.epicrisis.eye = item.answerValue ?? '';
        this.epicrisis.eyeScore = item.typeDataParameter.parameterData.map( (p: any) => p.valueObject).find( (e: any) => e.name?.value === item.answerValue)?.score?.value;
        break;
      default:
        break;
    }
  }

  /**
   * Sorulardan veriler toplandıktan sonra, çoklu sorulardan kullanılarak oluşturulan değişkenleri işler.
   */
  private organizeCases() {
    this.setPhysicalExamination();
    this.setSkinExamination();
    this.setHeadExamination();
    this.setRespiratoryExamination();
    this.setCardiovascularExamination();
    this.setAbdominalExamination();
    this.setNeurologicalExamination();
    this.setExtremes();
    this.setGksScore();
  }

  // ---------- Yardımcı Fonksiyonlar ----------

  private setPhysicalExamination(): void {
    const e = this.epicrisis;
    e.physicalExamination = `Pupiller : ${e.pupils} | Deri : ${e.skin} | Kurtarılma Yapıldı Mı? : ${e.isResqued} | Solunum : ${e.respiratory}`;
  }

  private setSkinExamination(): void {
    const e = this.epicrisis;
    e.skinExamination = this.getExaminationText(e.skinExaminationAnswer, e.skinExaminationDescription);
  }

  private setHeadExamination(): void {
    const e = this.epicrisis;
    e.headExamination = this.getExaminationText(e.headExaminationAnswer, e.headExaminationDescription);
  }

  private setRespiratoryExamination(): void {
    const e = this.epicrisis;
    e.respiratoryExamination = this.getExaminationText(e.respiratoryExaminationAnswer, e.respiratoryExaminationDescription);
  }

  private setCardiovascularExamination(): void {
    const e = this.epicrisis;
    e.cardiovascularExamination = this.getExaminationText(e.cardiovascularExaminationAnswer, e.cardiovascularExaminationDescription);
  }

  private setAbdominalExamination(): void {
    const e = this.epicrisis;
    e.abdominalExamination = this.getExaminationText(e.abdominalExaminationAnswer, e.abdominalExaminationDescription);
  }

  private setNeurologicalExamination(): void {
    const e = this.epicrisis;
    e.neurologicalExamination = this.getExaminationText(e.neurologicalExaminationAnswer, e.neurologicalExaminationDescription);
  }

  private setExtremes(): void {
    const e = this.epicrisis;
    e.extremes = this.getExaminationText(e.extremesAnswer, e.extremesDescription);
  }

  private setGksScore(): void {
    const e = this.epicrisis;
    const motorScore = Number(e.motorScore ?? 0);
    const verbalScore = Number(e.verbalScore ?? 0);
    const eyeScore = Number(e.eyeScore ?? 0);

    e.gksScore = (motorScore + verbalScore + eyeScore).toString();
    e.gksText = `${e.motor} (${e.motorScore ?? ''}) / ${e.verbal} (${e.verbalScore ?? ''}) / ${e.eye} (${e.eyeScore ?? ''})`;
  }

  // ---------- Yardımcı metot: Muayene metinlerini döndürür ----------
  private getExaminationText(answer: string | undefined, description: string | undefined): string {
    if (answer === 'Doğal_aciklama') {
      return this.getDefaultText(answer);
    } else if (answer === 'Doğal Değil_aciklama') {
      return description ?? '';
    } else {
      return '';
    }
  }

  private getDefaultText(answer: string): string {
    if (answer === 'Doğal_aciklama') {
      // Default açıklamaları burada eşle
      return 'Default doğal açıklama metni'; // Her muayene tipine göre ayrı default yazabilirsin
    } else {
      return '';
    }
  }

  sendEmail() {
    this.epicrisisMail.open(this.caseId, this.epicrisis?.patientInformations, 'email');
  }
  

  onMailClosed() {
  }

  handleEpicrisisSend(payload: SendActionPayload) {
    if (payload.type === 'email') {
      this.sendEpicrisisPdfMail(payload);
    }

  }

 private async sendEpicrisisPdfMail(payload: SendActionPayload) {
  const pdfBase64 = await this.helperService.generateEpicrisisPdf(
    this.approvedData,
    this.approvalDate,
    true
  );

  const filename = `Epikriz_${this.approvalDate.replaceAll('.', '-')}.pdf`;

  this.apiService.post('manuel-send-sms-mail/send-mail', {
    caseId: payload.caseId,
    emails: payload.receivers,
    subject: 'Epikriz Raporunuz',
    body: 'Epikriz raporunuz ektedir.',
    attachments: [
      {
        filename,
        content: pdfBase64, 
      }
    ],
    epicrisis: true,
  }).subscribe();
}



}
